import { Screen } from '@/interfaces/FlowInterfaces'
import { getFormChildren, sanitizeFlowJsonForMeta } from '@/utils/flowJsonShape'

export interface MetaFlowValidationResult {
  valid: boolean
  errors: string[]
}

const DATA_BINDING_REGEX = /\$\{data\.([^}]+)\}/g
const FORM_BINDING_REGEX = /\$\{form\.([^}]+)\}/g
const LEGACY_SCREEN_BINDING_REGEX = /\$\{screen\.[^}]+\}/

function ensureObject(value: unknown): Record<string, unknown> {
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }

  return {}
}

function defaultExampleForType(type: string, schema: Record<string, unknown>): unknown {
  const normalized = String(type).toLowerCase()

  if (normalized === 'boolean') {
    return false
  }

  if (normalized === 'number' || normalized === 'integer') {
    return 0
  }

  if (normalized === 'array') {
    const items = schema.items as Record<string, unknown> | undefined
    const properties = items?.properties as Record<string, unknown> | undefined

    if (properties && ('id' in properties || 'title' in properties)) {
      return [{ id: '1', title: 'Opción 1' }]
    }

    return [{ id: '1', title: 'Example' }]
  }

  if (normalized === 'object') {
    return {}
  }

  return 'Example'
}

function isValidExample(type: string, example: unknown): boolean {
  const normalized = String(type).toLowerCase()

  if (example === undefined || example === null) {
    return false
  }

  if (normalized === 'string' && typeof example === 'string') {
    return example.trim() !== ''
  }

  if (normalized === 'boolean') {
    return typeof example === 'boolean'
  }

  if (normalized === 'number' || normalized === 'integer') {
    return typeof example === 'number' && !Number.isNaN(example)
  }

  if (normalized === 'array') {
    return Array.isArray(example) && example.length > 0
  }

  if (normalized === 'object') {
    return typeof example === 'object' && !Array.isArray(example)
  }

  return example !== ''
}

function autofillDataSchema(schema: Record<string, unknown>): Record<string, unknown> {
  const next = { ...schema }
  const type = String(next.type || 'string')

  if (!isValidExample(type, next.__example__)) {
    next.__example__ = defaultExampleForType(type, next)
  }

  return next
}

export function autofillScreenDataExamples(flow: Record<string, unknown>): Record<string, unknown> {
  const cloned = JSON.parse(JSON.stringify(flow)) as Record<string, unknown>
  const screens = cloned.screens as Screen[] | undefined

  if (!Array.isArray(screens)) {
    return cloned
  }

  screens.forEach((screen) => {
    const data = ensureObject(screen.data)

    Object.keys(data).forEach((key) => {
      const schema = data[key]

      if (schema && typeof schema === 'object' && !Array.isArray(schema)) {
        data[key] = autofillDataSchema(schema as Record<string, unknown>)
      }
    })

    screen.data = data as Screen['data']
  })

  return cloned
}

function collectDataBindingKeys(value: unknown, keys: Set<string>): void {
  if (typeof value === 'string') {
    let match: RegExpExecArray | null
    const regex = new RegExp(DATA_BINDING_REGEX.source, 'g')

    while ((match = regex.exec(value)) !== null) {
      keys.add(match[1])
    }

    return
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectDataBindingKeys(item, keys))
    return
  }

  if (value && typeof value === 'object') {
    Object.values(value).forEach((item) => collectDataBindingKeys(item, keys))
  }
}

function getFooter(screen: Screen): Record<string, unknown> | null {
  const children = getFormChildren(screen)
  const footer = children[children.length - 1]

  if (footer?.type === 'Footer') {
    return footer as Record<string, unknown>
  }

  return null
}

function buildArraySchema(): Record<string, unknown> {
  return {
    type: 'array',
    items: {
      type: 'object',
      properties: {
        id: { type: 'string' },
        title: { type: 'string' }
      }
    },
    __example__: [{ id: '1', title: 'Opción 1' }]
  }
}

function buildStringSchema(): Record<string, unknown> {
  return {
    type: 'string',
    __example__: 'Example'
  }
}

export function validateMetaFlowJson(flow: Record<string, unknown>): MetaFlowValidationResult {
  const errors: string[] = []

  if (!flow?.version) {
    errors.push('El JSON debe incluir version.')
  }

  const screens = flow?.screens as Screen[] | undefined

  if (!Array.isArray(screens) || screens.length === 0) {
    errors.push('El JSON debe incluir al menos una pantalla en screens.')
    return { valid: false, errors }
  }

  if (JSON.stringify(flow).match(LEGACY_SCREEN_BINDING_REGEX)) {
    errors.push('Bindings legacy ${screen.*.form.*} no son válidos para Meta.')
  }

  screens.forEach((screen, screenIndex) => {
    const screenLabel = screen?.id || `pantalla ${screenIndex + 1}`
    const data = screen?.data

    if (Array.isArray(data)) {
      errors.push(`${screenLabel}: data debe ser un objeto, no un array.`)
    }

    const dataObject = ensureObject(data)
    const declaredDataKeys = new Set(Object.keys(dataObject))

    Object.entries(dataObject).forEach(([key, schema]) => {
      if (!schema || typeof schema !== 'object' || Array.isArray(schema)) {
        errors.push(`${screenLabel}: data.${key} debe ser un esquema con type.`)
        return
      }

      const typedSchema = schema as Record<string, unknown>
      const type = typedSchema.type

      if (!type) {
        errors.push(`${screenLabel}: data.${key} debe incluir type.`)
        return
      }

      if (!isValidExample(String(type), typedSchema.__example__)) {
        errors.push(`${screenLabel}: data.${key} requiere __example__ válido.`)
      }
    })

    const bindingKeys = new Set<string>()
    collectDataBindingKeys(screen, bindingKeys)

    getFormChildren(screen).forEach((child) => {
      collectDataBindingKeys(child, bindingKeys)

      if (
        typeof child?.['data-source'] === 'string' &&
        child['data-source'].includes('${data.')
      ) {
        const match = child['data-source'].match(/^\$\{data\.([^}]+)\}$/)

        if (match) {
          const dataKey = match[1]
          const schema = dataObject[dataKey] as Record<string, unknown> | undefined

          if (!schema) {
            errors.push(`${screenLabel}: data-source referencia data.${dataKey} no declarada.`)
          } else if (String(schema.type) !== 'array') {
            errors.push(`${screenLabel}: data.${dataKey} debe ser type array para data-source dinámico.`)
          }
        }
      }
    })

    bindingKeys.forEach((key) => {
      if (!declaredDataKeys.has(key)) {
        errors.push(`${screenLabel}: falta declarar data.${key} usada en bindings.`)
      }
    })

    const footer = getFooter(screen)

    if (footer?.['on-click-action']) {
      const action = footer['on-click-action'] as Record<string, unknown>
      const payload = ensureObject(action.payload)
      const actionName = String(action.name || '')

      if (actionName === 'navigate' && screenIndex < screens.length - 1) {
        const nextScreen = screens[screenIndex + 1]
        const nextData = ensureObject(nextScreen?.data)

        Object.entries(payload).forEach(([payloadKey, payloadValue]) => {
          if (typeof payloadValue === 'string' && FORM_BINDING_REGEX.test(payloadValue)) {
            if (!nextData[payloadKey]) {
              errors.push(
                `${screenLabel}: navigate payload "${payloadKey}" debe declararse en data de ${nextScreen?.id || 'siguiente pantalla'}.`
              )
            }
          }
        })
      }
    }
  })

  return {
    valid: errors.length === 0,
    errors
  }
}

export function prepareFlowJsonForMeta(flow: Record<string, unknown>): {
  flow: Record<string, unknown>
  validation: MetaFlowValidationResult
} {
  const autofilled = autofillScreenDataExamples(flow)
  const sanitized = sanitizeFlowJsonForMeta(autofilled)
  const validation = validateMetaFlowJson(sanitized)

  return {
    flow: sanitized,
    validation
  }
}

export function buildPayloadKey(screenIndex: number, fieldIndex: number, fieldName: string): string {
  return `screen_${screenIndex}_${fieldName}_${fieldIndex}`
}

export function buildDataSchemaForField(fieldType: string): Record<string, unknown> {
  if (fieldType === 'CheckboxGroup') {
    return buildArraySchema()
  }

  return buildStringSchema()
}
